import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, Heart, Settings, Menu, Users, Plus, User, MessageCircle, Crown, Search, HeartHandshake, Eye, MapPin, Calendar, Star, FileImage, Flame, Lock, Coins, Zap, Check, ArrowLeft, Trash2, Camera, Mic, HelpCircle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { MessageLimitModal } from "@/components/MessageLimitModal";
import { apiClient } from "@/api/ApiClient";

import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";


// Avatar logic removed - using URLs from server

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  imageUrl?: string;
  hasImage?: boolean;
  isBlurred?: boolean;
  messageExchangeId?: string;
}

// Import models from API package
import { BoyfriendProfile, ApiLastMessage, ApiChatItem, ApiChatsResponse, ApiMessage } from '@/api/models';

// Use BoyfriendProfile as Contact
type Contact = BoyfriendProfile;

interface ChatPageProps {
  user: any;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: () => void;
  onNavigateToProfilePage: (boyfriendId: string) => void;
  onPromptLogin: () => void;
  onLogout: () => void;
  newCharacter: any;
  selectedBoyfriendId: string | null;
  onTokensClick: () => void;
  userTokens: number;
  onPurchaseTokens: (amount: number, method: string) => void;
  favorites: Set<string>;
  onToggleFavorite: (boyfriendId: string) => Promise<void>;
  onBottomNavVisibilityChange?: (visible: boolean) => void;
  onBackToList: () => void;
}

export const ChatPage = ({ 
  user,
  onNavigateToMyBoyfriends,
  onNavigateToProfile, 
  onNavigateToSettings, 
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  onNavigateToProfilePage,
  onPromptLogin,
  onLogout,
  newCharacter,
  selectedBoyfriendId,
  onTokensClick,
  userTokens,
  onPurchaseTokens,
  favorites,
  onToggleFavorite,
  onBottomNavVisibilityChange,
  onBackToList
}: ChatPageProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);


  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitResetTime, setLimitResetTime] = useState<Date | null>(null);
  const [userStyle, setUserStyle] = useState<'anime' | 'realistic'>('realistic');
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Avatar URLs used directly from server

  // Initialize messages and contacts from API
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [chatContacts, setChatContacts] = useState<Contact[]>([]);

  const [selectedChat, setSelectedChat] = useState<string | null>(selectedBoyfriendId || null);
  const [swipedChatId, setSwipedChatId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Pagination state for infinite scroll
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null);

  // Command mapping: map translated text to commands
  const commandMap: Record<string, string> = {
    [t('chat.suggestion_send_photo')]: '#[SEND_PHOTO]#'
  };

  // Animated loading dots component
  const LoadingDots = () => (
    <div className="flex items-center space-x-1">
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );


  // Helper function to format message time
  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Load messages with pagination support
  // Returns true if chat was empty (for first message detection)
  const loadMessages = async (boyfriendId: string, beforeId?: string): Promise<boolean> => {
    try {
      const url = beforeId 
        ? `/chats/${boyfriendId}/messages?beforeId=${beforeId}`
        : `/chats/${boyfriendId}/messages`;
        
      const messagesData = await apiClient.get(url);
      console.log('ChatPage - messages data:', messagesData);
      console.log('First message structure:', messagesData[0]);
      
      if (messagesData && Array.isArray(messagesData)) {
        // Convert API messages to our Message format
        // Each API message contains both user message and AI response
        const formattedMessages: Message[] = [];
        
        messagesData.forEach((apiMsg: ApiMessage) => {
          console.log('Processing API message:', apiMsg);
          
          // Add AI response first (it will appear above user message with column-reverse)
          if (apiMsg.response || (apiMsg as any).imageUrl) {
            formattedMessages.push({
              id: `${apiMsg.id}_ai`,
              text: (apiMsg as any).imageUrl ? undefined as any : apiMsg.response,
              sender: 'ai',
              timestamp: new Date(apiMsg.createdAt),
              imageUrl: (apiMsg as any).imageUrl || undefined,
              hasImage: !!(apiMsg as any).imageUrl,
              isBlurred: false,
              messageExchangeId: apiMsg.messageExchangeId || apiMsg.id // Use message ID as messageExchangeId for now
            });
          }
          
          // Add user message second (it will appear below AI response with column-reverse)
          if (apiMsg.message) {
            // Remove command from beginning of message if present (format: #[COMMAND]# text)
            let displayText = apiMsg.message;
            const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
            if (commandMatch) {
              // Remove command and following space from display text
              displayText = displayText.substring(commandMatch[0].length);
            }
            
            formattedMessages.push({
              id: `${apiMsg.id}_user`,
              text: displayText,
              sender: 'user',
              timestamp: new Date(apiMsg.createdAt),
              imageUrl: undefined,
              hasImage: false,
              isBlurred: false
            });
          }
        });
        
        // Sort by timestamp (newest first) - because we use column-reverse
        formattedMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        if (beforeId) {
          // Loading more messages - append older messages to the end (they'll appear at top with column-reverse)
          setMessages(prev => ({
            ...prev,
            [boyfriendId]: [...(prev[boyfriendId] || []), ...formattedMessages]
          }));
        } else {
          // Initial load - replace messages (newest first)
          setMessages(prev => ({
            ...prev,
            [boyfriendId]: formattedMessages
          }));
        }
        
        // Update pagination state
        if (formattedMessages.length > 0) {
          // Use the original API message ID for beforeId (not the formatted message ID)
          const lastApiMessage = messagesData[messagesData.length - 1];
          setOldestMessageId(lastApiMessage.id);
          setHasMoreMessages(messagesData.length === 30); // Assuming 30 is the limit
          return false; // Chat is not empty
        } else {
          setHasMoreMessages(false);
          return true; // Chat is empty
        }
      } else {
        // No messages yet, initialize empty
        setMessages(prev => ({
          ...prev,
          [boyfriendId]: []
        }));
        setHasMoreMessages(false);
        return true; // Chat is empty
      }
    } catch (messageError) {
      console.error('Error loading messages:', messageError);
      // Initialize with empty messages on error
      setMessages(prev => ({
        ...prev,
        [boyfriendId]: []
      }));
      setHasMoreMessages(false);
      return true; // Consider empty on error
    }
  };

  // Load more messages (for infinite scroll)
  const loadMoreMessages = async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages || !oldestMessageId) return;
    
    setIsLoadingMore(true);
    try {
      await loadMessages(selectedChat, oldestMessageId);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Load specific boyfriend profile and messages
  const loadBoyfriendData = async () => {
    if (!user?.id || !selectedBoyfriendId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);
      
      // Load boyfriend profile
      const boyfriendData = await apiClient.get(`/boyfriends/${selectedBoyfriendId}`);
      console.log('ChatPage - boyfriend profile:', boyfriendData);
      
      if (boyfriendData) {
        setChatContacts([boyfriendData]);
        setSelectedContact(boyfriendData);
        
        // Load messages for this specific chat
        const isEmpty = await loadMessages(selectedBoyfriendId);
        
        // Check if chat is empty and send first message command
        if (isEmpty) {
          console.log('Chat is empty, sending first message command');
          // Automatically send first message command
          handleSendMessage('#[SEND_FIRST_MESSAGE]#');
        }
        
        // Mark messages as read
        try {
          await apiClient.post(`/chats/${selectedBoyfriendId}/mark-as-read`, {});
          console.log('Messages marked as read for boyfriend:', selectedBoyfriendId);
        } catch (markReadError) {
          console.error('Error marking messages as read:', markReadError);
          // Don't fail the whole load if marking as read fails
        }
      }
    } catch (error) {
      console.error('Error loading boyfriend data:', error);
      setApiError('Failed to load chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const [inputMessage, setInputMessage] = useState("");
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(favorites);
  const [showPrivateContentModal, setShowPrivateContentModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showTokenPaymentModal, setShowTokenPaymentModal] = useState(false);
  const [showPaymentFormModal, setShowPaymentFormModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'crypto'>('card');
  const [selectedTokenPackage, setSelectedTokenPackage] = useState<'small' | 'medium' | 'large'>('small');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);

  const handleSelectChat = (contactId: string) => {
    console.log('handleSelectChat called with contactId:', contactId);
    const contact = chatContacts.find(c => c.id === contactId);
    if (contact) {
      console.log('Setting selectedChat to:', contactId, 'and selectedContact to:', contact.name);
      setSelectedChat(contactId);
      setSelectedContact(contact);
      
      if (!isMobile && (window as any).collapseSidebar) {
        (window as any).collapseSidebar();
      }
    }
  };

  const handleDebugPrompt = (messageId: string) => {
    // Find the message to get its messageExchangeId
    const message = messages[selectedChat]?.find(msg => msg.id === messageId);
    console.log('Debug prompt clicked for message:', message);
    console.log('Message exchange ID:', message?.messageExchangeId);
    
    if (message?.messageExchangeId) {
      // Open debug page in new window
      window.open(`/debug/${message.messageExchangeId}`, '_blank');
    } else {
      toast({
        title: "Debug info not available",
        description: "This message doesn't have debug information available.",
        variant: "destructive"
      });
    }
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || !selectedChat) return;

    // Message limits are checked on backend in ChatController.checkChatLimits()
    // If limit is exceeded, backend will send premium-upgrade-required event

    // Check if this is a command - if mapped, prepend command to message text
    const command = commandMap[messageText];
    // If command exists, prepend it to the message text; otherwise use original text
    const apiText = command ? `${command} ${messageText}` : messageText;
    // Display original text in chat (for user-friendly display)
    const displayText = messageText;

    // For system commands like #[SEND_FIRST_MESSAGE]#, don't show user message
    const isSystemCommand = messageText.startsWith('#[') && messageText.endsWith(']#');
    
    const newMessage: Message = {
      id: Date.now().toString(),
      text: displayText,
      sender: 'user',
      timestamp: new Date()
    };

    // Add new message at the beginning (it will appear at bottom with column-reverse)
    // Skip adding user message for system commands
    if (!isSystemCommand) {
      setMessages(prev => ({
        ...prev,
        [selectedChat]: [newMessage, ...(prev[selectedChat] || [])]
      }));
    }

    setInputMessage("");

    // Call API for AI response using EventSource
    try {
      const aiMessageId = (Date.now() + 1).toString();
      let aiResponseText = '';
      
      // Add empty AI message first with loading indicator
      const aiMessage: Message = {
        id: aiMessageId,
        text: 'LOADING_DOTS', // Special marker for loading animation
        sender: 'ai',
        timestamp: new Date()
      };

      // Add AI loading message at the beginning (it will appear at bottom with column-reverse)
      setMessages(prev => ({
        ...prev,
        [selectedChat]: [aiMessage, ...(prev[selectedChat] || [])]
      }));

      // Get current access token
      const getAccessToken = () => {
        return apiClient.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('accessToken') : null);
      };

      // Create EventSource URL with token - send command + text
      const createEventSourceURL = (token: string | null) => {
        const baseUrl = `${apiClient.baseURL}/chat/stream?query=${encodeURIComponent(apiText)}&boyfriendId=${selectedChat}`;
        return token ? `${baseUrl}&token=${encodeURIComponent(token)}` : baseUrl;
      };

      // Create EventSource with token and retry logic
      const createEventSource = async (retryCount = 0): Promise<EventSource> => {
        const token = getAccessToken();
        const url = createEventSourceURL(token);
        
        console.log('Creating EventSource with URL:', url);
        return new EventSource(url);
      };

      let eventSource = await createEventSource();
      let messageExchangeId: string | null = null;
      
      // Listen for UUID event first
      eventSource.addEventListener('uuid', (event) => {
        messageExchangeId = event.data;
        console.log('Received messageExchangeId:', messageExchangeId);
        
        // Update the AI message with messageExchangeId
        setMessages(prev => ({
          ...prev,
          [selectedChat]: prev[selectedChat].map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, messageExchangeId: messageExchangeId }
              : msg
          )
        }));
      });

      // Listen for image event (chat random photo or first message avatar)
      eventSource.addEventListener('image', (event) => {
        try {
          const imageUrl = event.data as string;
          // Replace loading AI message with an image message - remove LOADING_DOTS text
          setMessages(prev => ({
            ...prev,
            [selectedChat]: prev[selectedChat].map(msg =>
              msg.id === aiMessageId
                ? { ...msg, imageUrl, hasImage: true, text: msg.text === 'LOADING_DOTS' ? undefined : msg.text }
                : msg
            )
          }));
        } catch (e) {
          console.error('Failed to handle image event', e);
        } finally {
          // Close EventSource after receiving image
          try { eventSource.close(); } catch {}
        }
      });

      // Listen for done event (completion signal)
      eventSource.addEventListener('done', () => {
        try {
          eventSource.close();
        } catch (e) {
          console.error('Failed to close EventSource on done event', e);
        }
      });

      // Listen for text event (first message greeting or other text responses)
      eventSource.addEventListener('text', (event) => {
        try {
          const text = (event as MessageEvent).data;
          // Replace loading AI message with text
          setMessages(prev => ({
            ...prev,
            [selectedChat]: prev[selectedChat].map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, text }
                : msg
            )
          }));
        } catch (e) {
          console.error('Failed to handle text event', e);
        }
      });

      // Listen for insufficient tokens event
      eventSource.addEventListener('insufficient-tokens', (event) => {
        try {
          const text = (event as MessageEvent).data || "Sorry, you don't have enough tokens.";
          // Replace the loading AI message with the insufficient tokens text
          setMessages(prev => ({
            ...prev,
            [selectedChat]: prev[selectedChat].map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, text }
                : msg
            )
          }));
        } catch (e) {
          console.error('Failed to handle insufficient-tokens event', e);
        } finally {
          try { eventSource.close(); } catch {}
          // Trigger global token modal
          try { window.dispatchEvent(new CustomEvent('open-token-modal')); } catch {}
          // Fallback: directly open via prop if available
          try { onTokensClick && onTokensClick(); } catch {}
        }
      });

      // Listen for premium upgrade required event
      eventSource.addEventListener('premium-upgrade-required', (event) => {
        try {
          const eventData = (event as MessageEvent).data;
          let messageText = "You've reached your message limit. Upgrade to Premium for unlimited chatting!";
          let resetTimeSeconds: number | null = null;
          
          // Try to parse JSON from event data
          try {
            const data = JSON.parse(eventData);
            if (data.message) {
              messageText = data.message;
            }
            if (data.resetTimeSeconds !== undefined) {
              resetTimeSeconds = parseInt(data.resetTimeSeconds, 10);
            }
          } catch (parseError) {
            // If not JSON, use as plain text
            if (eventData) {
              messageText = eventData;
            }
          }
          
          // Calculate reset time from current time + seconds (only if resetTimeSeconds is provided)
          if (resetTimeSeconds !== null && !isNaN(resetTimeSeconds)) {
            const resetTime = new Date(Date.now() + resetTimeSeconds * 1000);
            setLimitResetTime(resetTime);
          }
          
          // Replace the loading AI message with the premium upgrade text
          setMessages(prev => ({
            ...prev,
            [selectedChat]: prev[selectedChat].map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, text: messageText }
                : msg
            )
          }));
        } catch (e) {
          console.error('Failed to handle premium-upgrade-required event', e);
        } finally {
          try { eventSource.close(); } catch {}
          // Show premium upgrade modal
          setShowLimitModal(true);
        }
      });
      
      eventSource.onmessage = (event) => {
        try {
          // Check if it's the end marker
          if (event.data === '[DONE]') {
            eventSource.close();
            // Remove loading indicator and keep the actual text
            setMessages(prev => ({
              ...prev,
              [selectedChat]: prev[selectedChat].map(msg => 
                msg.id === aiMessageId 
                  ? { ...msg, text: aiResponseText || 'No response received.' }
                  : msg
              )
            }));
            return;
          }
          
          // Decode base64 plaintext data with proper UTF-8 handling
          try {
            const decodedBytes = atob(event.data);
            const decodedText = decodeURIComponent(escape(decodedBytes));
            aiResponseText += decodedText;
          } catch (decodeError) {
            // If base64 decoding fails, try raw data
            console.warn('Base64 decode failed, using raw data:', decodeError);
            aiResponseText += event.data;
          }
          
          // Update the AI message with streaming text (replace loading indicator)
          setMessages(prev => ({
            ...prev,
            [selectedChat]: prev[selectedChat].map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, text: aiResponseText }
                : msg
            )
          }));
        } catch (e) {
          console.error('Error processing SSE data:', e);
          // If all decoding fails, try to add the raw data
          if (event.data && event.data !== '[DONE]') {
            try {
              // Try base64 decode first
              const decodedBytes = atob(event.data);
              const decodedText = decodeURIComponent(escape(decodedBytes));
              aiResponseText += decodedText;
            } catch {
              // If base64 fails, use raw data
              aiResponseText += event.data;
            }
            
            setMessages(prev => ({
              ...prev,
              [selectedChat]: prev[selectedChat].map(msg => 
                msg.id === aiMessageId 
                  ? { ...msg, text: aiResponseText }
                  : msg
              )
            }));
          }
        }
      };

      eventSource.onerror = async (error) => {
        console.error('EventSource error:', error);
        
        // Check if it's a 401 error (unauthorized)
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('EventSource closed, checking if token refresh is needed...');
          
          // Try to refresh token and retry
          try {
            const newToken = await apiClient.refreshAccessToken();
            if (newToken) {
              console.log('Token refreshed, retrying EventSource...');
              eventSource.close();
              
              // Create new EventSource with refreshed token
              eventSource = await createEventSource();
              
              // Re-attach the same event handlers
              eventSource.addEventListener('uuid', (event) => {
                messageExchangeId = event.data;
                console.log('Received messageExchangeId (retry):', messageExchangeId);
                
                // Update the AI message with messageExchangeId
                setMessages(prev => ({
                  ...prev,
                  [selectedChat]: prev[selectedChat].map(msg => 
                    msg.id === aiMessageId 
                      ? { ...msg, messageExchangeId: messageExchangeId }
                      : msg
                  )
                }));
              });
              
              eventSource.onmessage = (event) => {
                try {
                  // Check if it's the end marker
                  if (event.data === '[DONE]') {
                    eventSource.close();
                    // Remove loading indicator and keep the actual text
                    setMessages(prev => ({
                      ...prev,
                      [selectedChat]: prev[selectedChat].map(msg => 
                        msg.id === aiMessageId 
                          ? { ...msg, text: aiResponseText || 'No response received.' }
                          : msg
                      )
                    }));
                    return;
                  }
                  
                  // Decode base64 plaintext data with proper UTF-8 handling
                  try {
                    const decodedBytes = atob(event.data);
                    const decodedText = decodeURIComponent(escape(decodedBytes));
                    aiResponseText += decodedText;
                  } catch (decodeError) {
                    // If base64 decoding fails, try raw data
                    console.warn('Base64 decode failed, using raw data:', decodeError);
                    aiResponseText += event.data;
                  }
                  
                  // Update the AI message with streaming text (replace loading indicator)
                  setMessages(prev => ({
                    ...prev,
                    [selectedChat]: prev[selectedChat].map(msg => 
                      msg.id === aiMessageId 
                        ? { ...msg, text: aiResponseText }
                        : msg
                    )
                  }));
                } catch (e) {
                  console.error('Error processing SSE data:', e);
                  // If all decoding fails, try to add the raw data
                  if (event.data && event.data !== '[DONE]') {
                    try {
                      // Try base64 decode first
                      const decodedBytes = atob(event.data);
                      const decodedText = decodeURIComponent(escape(decodedBytes));
                      aiResponseText += decodedText;
                    } catch {
                      // If base64 fails, use raw data
                      aiResponseText += event.data;
                    }
                    
                    setMessages(prev => ({
                      ...prev,
                      [selectedChat]: prev[selectedChat].map(msg => 
                        msg.id === aiMessageId 
                          ? { ...msg, text: aiResponseText }
                          : msg
                      )
                    }));
                  }
                }
              };

              eventSource.onerror = async (retryError) => {
                console.error('EventSource retry error:', retryError);
                eventSource.close();
                
                // Final fallback to error message
                setMessages(prev => ({
                  ...prev,
                  [selectedChat]: prev[selectedChat].map(msg => 
                    msg.id === aiMessageId 
                      ? { ...msg, text: "Sorry, I'm having trouble responding right now. Please try again." }
                      : msg
                  )
                }));
              };

              return; // Successfully retried, exit early
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }
        
        // If we get here, either no refresh token or refresh failed
        eventSource.close();
        
        // Fallback to error message
        setMessages(prev => ({
          ...prev,
          [selectedChat]: prev[selectedChat].map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, text: "Sorry, I'm having trouble responding right now. Please try again." }
              : msg
          )
        }));
      };

      // Close after 30 seconds to prevent hanging
      setTimeout(() => {
        eventSource.close();
      }, 30000);

    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Fallback to hardcoded response on error
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I'm having trouble responding right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => ({
        ...prev,
        [selectedChat]: [...(prev[selectedChat] || []), aiResponse]
      }));
    }
  };


  const handleToggleFavoriteLocal = async (contactId: string) => {
    setLocalFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(contactId)) {
        newFavorites.delete(contactId);
      } else {
        newFavorites.add(contactId);
      }
      return newFavorites;
    });
    
    // Also call the parent handler
    await onToggleFavorite(contactId);
  };

  const handlePremiumUpgrade = () => {
    setShowUpgradeModal(false);
    setShowPaymentModal(true);
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: 'card' | 'crypto') => {
    // Silent subscription activation - no notification
    setShowPaymentModal(false);
  };

  const suggestions = selectedContact ? [
    t('chat.suggestion_tell_me_day'),
    t('chat.suggestion_favorite_memory'),
    t('chat.suggestion_missed_you'),
    t('chat.suggestion_thinking_about')
  ] : [];

  // Load boyfriend data on mount
  useEffect(() => {
    loadBoyfriendData();
    // Reset pagination state when switching chats
    setOldestMessageId(null);
    setHasMoreMessages(true);
    setIsLoadingMore(false);
  }, [user?.id, selectedBoyfriendId]);

  // With column-reverse, scroll is automatically at the bottom (scrollTop = 0)
  // No need for manual scrolling - new messages automatically appear at the bottom

  // Set selectedChat when selectedBoyfriendId changes
  useEffect(() => {
    if (selectedBoyfriendId) {
      setSelectedChat(selectedBoyfriendId);
    }
  }, [selectedBoyfriendId]);

  // When nothing is selected, navigate back to list outside of render
  // BUT NOT if we're in error state or still loading
  useEffect(() => {
    if ((!selectedChat || !selectedContact) && !apiError && !isLoading) {
      console.log('Navigating back to list - selectedChat:', selectedChat, 'selectedContact:', selectedContact, 'isLoading:', isLoading);
      onBackToList();
    }
  }, [selectedChat, selectedContact, onBackToList, apiError, isLoading]);

  // Control bottom navigation visibility - hide when in conversation on mobile
  useEffect(() => {
    if (onBottomNavVisibilityChange && isMobile) {
      onBottomNavVisibilityChange(false);
    }
    
    // Cleanup: restore nav visibility when component unmounts
    return () => {
      if (onBottomNavVisibilityChange && isMobile) {
        onBottomNavVisibilityChange(true);
      }
    };
  }, [isMobile, onBottomNavVisibilityChange]);

  const isLoggedIn = !!user;

  if (!isLoggedIn) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Sign in to start chatting</h2>
            <p className="text-muted-foreground mb-6">
              Connect with your AI boyfriends and enjoy personalized conversations
            </p>
            <Button variant="romantic" onClick={onPromptLogin}>
              <User className="h-4 w-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-4">Loading chats...</h2>
            <p className="text-muted-foreground">
              Please wait while we load your conversations
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (apiError) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Error loading chats</h2>
            <p className="text-muted-foreground mb-6">{apiError}</p>
            <Button variant="romantic" onClick={loadBoyfriendData}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If no chat is selected, render nothing (navigation handled in effect)
  // BUT NOT if we're in error state - show error instead
  if ((!selectedChat || !selectedContact) && !apiError) {
    return null;
  }

  return (
    <div className="bg-gradient-dreamy h-full overflow-hidden flex flex-col">
      {/* Main Chat Content */}
      <div className="flex-1 flex min-h-0">
        {/* Chat Area */}
          <div className={`flex-1 flex flex-col relative min-h-0 ${isMobile ? 'w-full' : 'min-w-0 overflow-hidden max-w-full'}`}>
            {selectedChat && selectedContact ? (
              <>
                {/* Chat Header */}
                <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                       {/* Mobile back button */}
                       {isMobile && (
                          <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                            onClick={onBackToList}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <div className="relative shrink-0">
                        <Avatar 
                          className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} border-2 border-primary/20 cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all`}
                          onClick={() => onNavigateToProfilePage(selectedContact.id)}
                        >
                          <AvatarImage 
                            src={selectedContact.avatarUrl} 
                            alt={selectedContact.name} 
                          />
                          <AvatarFallback>{selectedContact.name[0]}</AvatarFallback>
                        </Avatar>
                        {/* Always show online indicator */}
                        <div className={`absolute -bottom-0.5 -right-0.5 ${isMobile ? 'w-2.5 h-2.5' : 'w-4 h-4'} bg-green-500 rounded-full border border-background`}></div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 
                          className={`font-semibold truncate cursor-pointer hover:text-primary transition-colors ${isMobile ? 'text-sm' : ''}`}
                          onClick={() => onNavigateToProfilePage(selectedContact.id)}
                        >
                          {selectedContact.name}
                        </h3>
                        <p className={`text-muted-foreground truncate flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {t('chat.online')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`transition-all duration-300 hover:scale-110 ${isMobile ? 'h-8 w-8' : ''}`}
                        onClick={async () => {
                          if (!selectedContact) return;
                          try {
                            setGalleryLoading(true);
                            const list = await apiClient.getUserGeneratedImagesByBoyfriend(selectedContact.id);
                            const urls = Array.isArray(list) ? list.map((it: any) => it.imageUrl).filter(Boolean) : [];
                            setGalleryImages(urls);
                            setShowGalleryModal(true);
                          } catch (e) {
                            toast({ title: 'Failed to load gallery', variant: 'destructive' });
                          } finally {
                            setGalleryLoading(false);
                          }
                        }}
                      >
                        <FileImage className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`transition-all duration-300 hover:scale-110 ${isMobile ? 'h-8 w-8' : ''} ${
                          favorites.has(selectedContact.id) 
                            ? 'text-red-500 hover:text-red-600 bg-red-500/10 hover:bg-red-500/20 ring-2 ring-red-500/30 shadow-lg shadow-red-500/25' 
                            : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/10 hover:shadow-lg hover:shadow-red-500/25'
                        }`}
                        onClick={() => handleToggleFavoriteLocal(selectedContact.id)}
                      >
                        <Heart className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} transition-all duration-300 ${
                          favorites.has(selectedContact.id) 
                            ? 'fill-current scale-110 drop-shadow-lg' 
                            : 'fill-none hover:fill-current/30'
                        }`} />
                      </Button>
                      {!isMobile && (
                        <Button variant="ghost" size="icon">
                          <Settings className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div 
                  ref={messagesContainerRef}
                  className={`chat-messages-container flex flex-col-reverse flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isMobile ? 'px-2 py-2 pb-36' : 'px-4 py-3 pb-40'}`}
                  style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--primary) / 0.3) hsl(var(--muted))' }}
                  onScroll={(e) => {
                    const element = e.target as HTMLDivElement;
                    // Load more when scrolled to bottom (which is scrollHeight with column-reverse)
                    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
                    if (isAtBottom && hasMoreMessages && !isLoadingMore) {
                      loadMoreMessages();
                    }
                  }}
                >
                  {/* Load More Button */}
                  {hasMoreMessages && (
                    <div className="flex justify-center py-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={loadMoreMessages}
                        disabled={isLoadingMore}
                        className="text-xs"
                      >
                        {isLoadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-primary mr-2"></div>
                            Loading...
                          </>
                        ) : (
                          'Load older messages'
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {(messages[selectedChat] || []).length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-full text-center px-4 py-12">
                      <MessageCircle className="h-16 w-16 text-muted-foreground/50 mb-4" />
                      <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                        You have no conversation started yet
                      </h3>
                      <p className="text-sm text-muted-foreground/70 max-w-sm">
                        Start chatting with {selectedContact?.name || 'your boyfriend'} to begin your conversation!
                      </p>
                    </div>
                  ) : (
                    (messages[selectedChat] || []).map((message, index, array) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} ${index < array.length - 1 ? 'mb-2' : ''}`}
                    >
                       <div
                         className={`${isMobile ? 'max-w-[85%]' : 'max-w-[75%]'} ${
                           message.sender === 'user'
                             ? 'bg-primary text-primary-foreground'
                             : 'bg-gray-200 dark:bg-gray-700'
                         } ${message.imageUrl ? 'p-1' : 'px-3 py-2'} rounded-2xl`}
                         style={{ 
                           wordBreak: 'break-word',
                           overflowWrap: 'break-word',
                           hyphens: 'auto'
                         }}
                       >
                         {message.imageUrl && (
                           <div className="mb-2">
                            <img
                              src={message.imageUrl}
                              alt="Shared photo"
                              className="w-full h-auto rounded-lg max-w-[250px] cursor-zoom-in"
                              onClick={() => { setActiveImageUrl(message.imageUrl!); setShowImageModal(true); }}
                            />
                           </div>
                         )}
                          {message.text && (
                            <div className={`${message.imageUrl ? 'px-2 pb-2' : ''} relative`}>
                              {message.text === 'LOADING_DOTS' ? (
                                <div className="flex items-center pr-14">
                                  <LoadingDots />
                                  <span className="invisible">&nbsp;</span>
                                </div>
                              ) : (
                                <p className={`${isMobile ? 'text-sm' : 'text-base'} leading-relaxed whitespace-pre-wrap pr-14`}>
                                  {(() => {
                                    // Remove command from beginning if present (for user messages loaded from API)
                                    let displayText = message.text;
                                    if (message.sender === 'user') {
                                      const commandMatch = displayText.match(/^#\[([A-Z_]+)\]#\s*/);
                                      if (commandMatch) {
                                        displayText = displayText.substring(commandMatch[0].length);
                                      }
                                    }
                                    return displayText;
                                  })()}
                                </p>
                              )}
                              <span className={`absolute bottom-0 right-0 text-xs ${
                                message.sender === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                          {!message.text && (
                            <div className={`${message.imageUrl ? 'px-2 pb-1' : ''} flex justify-end`}>
                              <p className={`text-xs ${message.imageUrl ? '' : 'mt-1'} ${
                                message.sender === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          )}
                       </div>
                    </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className={`${isMobile ? 'fixed' : 'absolute'} bottom-0 left-0 right-0 bg-card/40 backdrop-blur-sm px-4 py-3 pb-4 shrink-0 z-10`}>
                  {/* Message Suggestions */}
                  {suggestions.length > 0 && (
                    <div className="pb-3 mb-3 border-b border-primary/20 -mx-4 px-4">
                      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                        {/* Priority suggestions */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-auto py-1.5 px-2 border border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 backdrop-blur-sm shrink-0 flex items-center gap-1"
                          onClick={() => {
                            handleSendMessage(t('chat.suggestion_send_photo'));
                          }}
                        >
                          <Camera className="h-3 w-3" />
                          {t('chat.photo')}
                        </Button>
                        {suggestions.map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="text-xs h-auto py-1.5 px-2 border border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 backdrop-blur-sm shrink-0"
                            onClick={() => {
                              handleSendMessage(suggestion);
                            }}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(inputMessage);
                        }
                      }}
                      placeholder={`${t('chat.type_message')} ${selectedContact.name}...`}
                      className={`flex-1 bg-muted border-primary/20 focus:border-primary placeholder:text-muted-foreground/60 ${isMobile ? 'text-sm' : ''}`}
                    />
                    <Button
                      onClick={() => handleSendMessage(inputMessage)}
                      disabled={!inputMessage.trim()}
                      variant="romantic"
                      size="icon"
                      className={`shrink-0 ${isMobile ? 'h-9 w-9' : ''}`}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* No Chat Selected */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                  {chatContacts.length === 0 ? (
                    <>
                      <div className="bg-card/50 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                        <Plus className="h-12 w-12 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4">No conversations yet</h3>
                      <p className="text-muted-foreground mb-6">
                        Create your first AI boyfriend to start chatting
                      </p>
                      {isLoggedIn ? (
                        <Button variant="romantic" onClick={onNavigateToCharacterCreation}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Boyfriend
                        </Button>
                      ) : (
                        <Button variant="romantic" onClick={onPromptLogin}>
                          <User className="h-4 w-4 mr-2" />
                          Sign In to Get Started
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">Select a conversation</h3>
                      <p className="text-muted-foreground">
                        Choose a conversation from the sidebar to start chatting
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Section - Show only on desktop when contact is selected */}
          {!isMobile && selectedContact && (
            <div className="w-80 bg-card/60 backdrop-blur-sm border-l border-primary/20 flex flex-col">
              {/* Profile Header */}
              <div className="relative flex-1">
                <div className="h-full relative">
                  <img
                    src={selectedContact.avatarUrl}
                    alt={selectedContact.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedContact.isOnline && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-green-500 rounded-full border-4 border-white shadow-lg"></div>
                  )}
                  
                  {/* Profile overlay info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                    <h2 className="text-2xl font-bold text-white mb-2">{selectedContact.name}</h2>
                    <p className="text-white/90 text-sm mb-3">{selectedContact.bio}</p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <Calendar className="h-4 w-4" />
                        <span>{selectedContact.age} years old</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <MapPin className="h-4 w-4" />
                        <span>{selectedContact.location}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mt-3">
                      {selectedContact.interests.map((interest, index) => (
                        <Badge key={index} variant="secondary" className="text-xs bg-white/20 text-white border-white/30">
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Actions */}
              <div className="p-4 border-t border-primary/20 space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowPrivateContentModal(true)}
                >
                  <FileImage className="h-4 w-4 mr-2" />
                  View Photos
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={async () => {
                    if (!selectedContact) return;
                    try {
                      setGalleryLoading(true);
                      const list = await apiClient.getUserGeneratedImagesByBoyfriend(selectedContact.id);
                      const urls = Array.isArray(list) ? list.map((it: any) => it.imageUrl).filter(Boolean) : [];
                      setGalleryImages(urls);
                      setShowGalleryModal(true);
                    } catch (e) {
                      toast({ title: 'Failed to load gallery', variant: 'destructive' });
                    } finally {
                      setGalleryLoading(false);
                    }
                  }}
                >
                  <FileImage className="h-4 w-4 mr-2" />
                  TODO view gallery
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start"
                >
                  <Star className="h-4 w-4 mr-2" />
                  Rate Conversation
                </Button>
              </div>
            </div>
          )}
        </div>

      {/* Token Balance Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-500" />
              Token Balance
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center p-6 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20">
              <Coins className="h-12 w-12 mx-auto mb-3 text-amber-500" />
              <div className="text-3xl font-bold text-amber-600">{userTokens}</div>
              <div className="text-sm text-muted-foreground">Available Tokens</div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">Send a message</span>
                <span className="text-sm font-medium">1 token</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">Request a photo</span>
                <span className="text-sm font-medium">5 tokens</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card/50 rounded-lg">
                <span className="text-sm">Voice message</span>
                <span className="text-sm font-medium">3 tokens</span>
              </div>
            </div>
            
            <Button
              variant="romantic"
              className="w-full"
              onClick={() => {
                setShowTokenModal(false);
                setShowTokenPaymentModal(true);
              }}
            >
              <Zap className="h-4 w-4 mr-2" />
              Purchase More Tokens
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Crown className="h-6 w-6 text-amber-500" />
              Upgrade to Premium
            </DialogTitle>
            <DialogDescription>
              Unlock unlimited conversations and exclusive features
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'monthly' ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
            }`} onClick={() => setSelectedPlan('monthly')}>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Monthly Plan</h3>
                <div className="text-3xl font-bold text-primary mb-4">$9.99</div>
                <p className="text-sm text-muted-foreground mb-6">per month</p>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Unlimited messages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Premium content access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Priority support</span>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'yearly' ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
            }`} onClick={() => setSelectedPlan('yearly')}>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Yearly Plan</h3>
                <div className="text-3xl font-bold text-primary mb-1">$99.99</div>
                <div className="text-sm text-green-600 font-medium mb-3">Save 17%</div>
                <p className="text-sm text-muted-foreground mb-6">per year</p>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Everything in Monthly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Exclusive yearly bonuses</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="space-y-4">
            <Button 
              onClick={handlePremiumUpgrade}
              variant="romantic" 
              size="lg" 
              className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to {selectedPlan === 'monthly' ? 'Monthly' : 'Yearly'} Plan
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Secure payment • Cancel anytime
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Private Content Modal */}
      {selectedContact && (
        <Dialog open={showPrivateContentModal} onOpenChange={setShowPrivateContentModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pr-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-red-500" />
                  <DialogTitle>{selectedContact.name}'s Private Content</DialogTitle>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-lg border border-amber-500/20">
                    <Coins className="h-4 w-4 text-amber-500" />
                    <span className="font-semibold">{userTokens}</span>
                    <span className="text-muted-foreground text-sm">tokens</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setShowPrivateContentModal(false);
                      setShowTokenModal(true);
                    }}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Buy Tokens
                  </Button>
                </div>
              </div>
            </DialogHeader>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="relative group">
                  <div className="aspect-square bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg flex items-center justify-center border border-primary/20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <Lock className="h-8 w-8 mx-auto mb-2 text-white" />
                        <p className="text-white text-sm font-medium">5 tokens</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="romantic" 
                    size="sm" 
                    className="w-full mt-2"
                    disabled={userTokens < 5}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Unlock (5 tokens)
                  </Button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Gallery Modal */}
      {selectedContact && (
        <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-2 rounded-full"
              onClick={() => setShowGalleryModal(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <DialogHeader>
              <DialogTitle>{selectedContact.name} – Gallery</DialogTitle>
              <DialogDescription>
                Photos generated with this boyfriend.
              </DialogDescription>
            </DialogHeader>
            {galleryLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            ) : galleryImages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">No photos yet.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {galleryImages.map((url, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden border border-primary/20">
                    <img 
                      src={url} 
                      alt={`Generated ${idx}`} 
                      className="w-full h-auto object-cover cursor-zoom-in"
                      onClick={() => { setActiveImageUrl(url); setShowImageModal(true); }}
                    />
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Image Zoom Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Image preview</DialogTitle>
          </DialogHeader>
          <div className="w-full h-full flex items-center justify-center">
            {activeImageUrl && (
              <img src={activeImageUrl} alt="Zoomed" className="max-h-[80vh] w-auto h-auto rounded-lg" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <SubscriptionPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        selectedPlan={selectedPlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Chat?</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this chat? This action cannot be undone and all messages will be lost.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setChatToDelete(null);
                setSwipedChatId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => {
                if (chatToDelete) {
                  // Handle actual delete logic here
                  console.log('Permanently deleting chat:', chatToDelete);
                  // You can add the actual deletion logic here
                }
                setDeleteConfirmOpen(false);
                setChatToDelete(null);
                setSwipedChatId(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Limit Modal - only show if resetTime is available */}
      {limitResetTime && (
        <MessageLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          onUpgrade={onNavigateToPremium}
          resetTime={limitResetTime}
        />
      )}
    </div>
  );
};

// Swipeable Chat Component
interface SwipeableChatProps {
  contact: any;
  isSelected: boolean;
  onSelect: () => void;
  onSwipe: (id: string) => void;
  onDelete: (id: string) => void;
  localFavorites: Set<string>;
  userStyle: 'anime' | 'realistic';
}

const SwipeableChat: React.FC<SwipeableChatProps> = ({
  contact,
  isSelected,
  onSelect,
  onSwipe,
  onDelete,
  localFavorites,
  userStyle
}) => {
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setCurrentX(0);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const touchX = e.touches[0].clientX;
    const diffX = startX - touchX;
    
    // Only allow left swipe (positive diffX)
    if (diffX > 0) {
      setCurrentX(Math.min(diffX, 80)); // Max swipe distance of 80px
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (currentX > 40) {
      // Show delete button
      onSwipe(contact.id);
      setCurrentX(80);
    } else {
      // Reset position
      setCurrentX(0);
      if (isSelected) {
        onSwipe('');
      }
    }
  };

  const handleClick = () => {
    if (isSelected) {
      onSwipe('');
    } else if (currentX === 0) {
      onSelect();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(contact.id);
  };

  useEffect(() => {
    if (!isSelected) {
      setCurrentX(0);
    }
  }, [isSelected]);

  // Avatar URLs used directly from server

  return (
    <div className="relative overflow-hidden">
      <div
        ref={containerRef}
        className="p-3 border-b border-primary/10 hover:bg-card/50 cursor-pointer transition-all duration-300"
        style={{
          transform: `translateX(-${currentX}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <Avatar className="h-14 w-14 border-2 border-primary/20">
              <AvatarImage 
                src={contact.avatarUrl} 
                alt={contact.name} 
              />
              <AvatarFallback>{contact.name[0]}</AvatarFallback>
            </Avatar>
            {contact.isOnline && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-background"></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {/* First line: Name and time */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <h3 className="font-semibold text-sm truncate">{contact.name}</h3>
                {localFavorites.has(contact.id) && (
                  <Heart className="h-3 w-3 text-red-500 fill-red-500" />
                )}
              </div>
              <span className="text-xs text-muted-foreground">{contact.lastMessageTime}</span>
            </div>
            {/* Second line: Last message and unread count */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground truncate flex-1 mr-2">
                {(() => {
                  // Remove command from beginning if present
                  let text = contact.lastMessage || "";
                  const commandMatch = text.match(/^#\[([A-Z_]+)\]#\s*/);
                  if (commandMatch) {
                    text = text.substring(commandMatch[0].length);
                  }
                  return text;
                })()}
              </p>
              {contact.unreadCount && contact.unreadCount > 0 && (
                <Badge variant="secondary" className="text-xs h-4 bg-primary text-primary-foreground">
                  {contact.unreadCount}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Button */}
      <div 
        className="absolute right-0 top-0 h-full w-20 bg-red-500 flex items-center justify-center"
        style={{
          transform: `translateX(${80 - currentX}px)`,
        }}
      >
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:text-white hover:bg-red-600"
          onClick={handleDelete}
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </div>

    </div>
  );
};
